import { faEquals, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import FormField from 'containers/FormField/FormField';
import { setTotal } from 'forms/DosingInfo/helpers/setTotal';
import { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import './DosingRow.scss';

interface Props {
  fields: {
    doses: string[];
    totalDose?: string;
    timeOfDrugTaking?: string;
  };
  title?: string;
  withOperators?: boolean;
  wideField?: boolean | number;
  mobileFullWidth?: boolean;
  timeOfDrugTakingInformation?: boolean;
}
const DosingRow = ({
  fields,
  title,
  withOperators,
  wideField,
  mobileFullWidth,
  timeOfDrugTakingInformation,
}: Props) => {
  const { setValue, getValues, clearErrors, watch, trigger } = useFormContext();
  const [required, setRequired] = useState(false);
  const { doses, totalDose, timeOfDrugTaking } = fields;
  const dosesWatchers = watch(doses);
  const totalWatcher = totalDose ? watch(totalDose) : '';
  const timeWatcher = timeOfDrugTaking ? watch(timeOfDrugTaking) : '';

  useEffect(() => {
    if (totalDose) {
      setValue(totalDose, setTotal(getValues(doses)));
      trigger(totalDose);
    }
  }, [...dosesWatchers, totalWatcher, timeWatcher]);

  useEffect(() => {
    const setDosingRowRequired = async () => {
      const arr = [...doses];
      if (totalDose) {
        arr.push(totalDose);
      }
      if (timeOfDrugTaking) {
        arr.push(timeOfDrugTaking);
      }

      if (getValues(arr).find((field) => field !== '')) {
        setRequired(true);
      }
      if (getValues(arr).every((field) => field === '')) {
        if (required) {
          await trigger(arr);
          clearErrors(arr);
        }
        setRequired(false);
      }
    };
    setDosingRowRequired();
  }, [...dosesWatchers, totalWatcher, timeWatcher]);

  return (
    <Row className="py-2 g-0">
      {title && (
        <Col
          xs={12}
          lg={2}
          className="text-muted mt-lg-4 mb-2 "
        >
          {title}
        </Col>
      )}
      {doses.map((field, index) => {
        return (
          <Col
            key={field}
            data-testid="containerForNumberField"
            xs={mobileFullWidth && !wideField ? 12 : 6}
            md={3}
            lg={2}
            className="d-flex mb-2"
          >
            <div className="input-group input-group-sm align-content-start border-2">
              <FormField
                type="text"
                variant={'number'}
                units="g"
                identifier={field}
                required={required}
                hideReq={required}
              />
            </div>
            {withOperators && index != doses.length - 1 && (
              <div
                data-testid="operator"
                className="float-end px-2 px-md-3 operator"
              >
                <FontAwesomeIcon
                  icon={faPlus}
                  color="black"
                />
              </div>
            )}
            {withOperators && index == doses.length - 1 && (
              <div
                data-testid="operator"
                className="float-end px-2 px-md-3 operator"
              >
                <FontAwesomeIcon
                  icon={faEquals}
                  color="black"
                />
              </div>
            )}
            {!withOperators && !mobileFullWidth && (
              <div className="px-2 px-md-3">
                <div style={{ width: '14px' }}></div>
              </div>
            )}
          </Col>
        );
      })}
      {totalDose && (
        <Col
          data-testid="containerForNumberField"
          className={`d-flex`}
          xs={mobileFullWidth ? 12 : 6}
          md={wideField ? 4 : 3}
          lg={3}
        >
          <div className="input-group input-group-sm align-content-start">
            <FormField
              type="text"
              variant={'numberAsText'}
              readOnly={true}
              units="g"
              identifier={totalDose}
              required={required}
              hideReq={required}
            />
          </div>
          {withOperators && (
            <div className="px-2 px-md-3">
              <div style={{ width: '14px' }}></div>
            </div>
          )}
        </Col>
      )}
      {timeOfDrugTaking && (
        <Col
          xs={6}
          md={3}
          lg={3}
          className="position-relative d-flex"
        >
          {timeOfDrugTakingInformation && (
            <div className="text-muted time-of-drug-taking-information">
              Number of Days (at each titration step)
            </div>
          )}
          <div className="input-group input-group-sm align-content-start">
            <FormField
              identifier={timeOfDrugTaking}
              step={1}
              wholeNumber
              type="text"
              variant="number"
              units="days"
              required={required}
              hideReq={required}
              style={{ height: 'auto' }}
            />
          </div>
          <div className="px-2 px-md-3 d-sm-block d-md-none">
            <div style={{ width: '14px' }}></div>
          </div>
        </Col>
      )}
    </Row>
  );
};

export default DosingRow;
